import React, { useState } from 'react';
import { X } from 'lucide-react';
import { authAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const REASONS: Array<{ value: string; label: string }> = [
  { value: 'not_using', label: '잘 사용하지 않아요' },
  { value: 'missing_features', label: '필요한 기능이 없어요' },
  { value: 'privacy', label: '개인정보가 걱정돼요' },
  { value: 'other', label: '기타' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * 회원 탈퇴 — 무엇이 지워지는지 보여 주고 비밀번호로 한 번 더 확인한다(되돌릴 수 없음).
 * 서버 처리: DELETE /api/auth/account → services/accountDeletion.ts
 */
const DeleteAccountModal: React.FC<Props> = ({ open, onClose }) => {
  const { logout } = useAuth();
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed || !password || loading) return;
    setLoading(true);
    setError('');
    try {
      await authAPI.deleteAccount(password, reason || undefined);
      // 이 기기에 남은 정보도 지운다(로그인 정보·최근 AI 질문·조회 기록)
      try {
        localStorage.removeItem('ai_recent_queries');
        sessionStorage.removeItem('pulse_viewed_posts');
      } catch {
        // 저장소 접근 불가 환경
      }
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || '탈퇴 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    await logout(); // 서버 세션은 이미 삭제됨 — 이 기기의 로그인 정보만 정리
    window.location.href = '/';
  };

  return (
    // 프로필 창 배경(클릭 시 닫힘) 안에 렌더되므로 클릭이 위로 올라가지 않게 막는다 — 막지 않으면 확인 창이 프로필 창과 함께 닫혔다
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">회원 탈퇴</h2>
          {!done && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {done ? (
          <div className="px-6 py-8 text-center">
            <p className="font-semibold text-gray-900 mb-2">탈퇴가 완료되었습니다.</p>
            <p className="text-sm text-gray-600 mb-6">그동안 Pulse를 이용해 주셔서 감사합니다.</p>
            <button onClick={finish} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 min-h-[44px]">
              홈으로
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <div className="text-sm text-gray-700 space-y-2">
              <p>탈퇴하면 아래 정보가 <strong>즉시 삭제되며 되돌릴 수 없습니다.</strong></p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li>계정과 프로필·경력·추천서</li>
                <li>작성한 게시물(달린 댓글·좋아요 포함)과 댓글</li>
                <li>면허 검증 정보, 채널, 채용 지원서, API 키, 알림</li>
                <li>올린 이미지와 파일</li>
              </ul>
              <p className="text-xs text-gray-500">
                내 댓글에 다른 회원이 단 답글은 일반 댓글로 남습니다. 백업에는 최대 14일간 남았다가 자동으로 삭제됩니다.{' '}
                <a href="/privacy.html#s6" target="_blank" rel="noopener noreferrer" className="underline">개인정보 처리방침</a>
              </p>
            </div>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">떠나시는 이유 (선택)</span>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                <option value="">선택하지 않음</option>
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                required
              />
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" />
              <span>위 내용을 확인했으며 탈퇴합니다.</span>
            </label>

            {error && <p className="text-sm text-red-600 whitespace-pre-wrap">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 min-h-[44px]">
                취소
              </button>
              <button
                type="submit"
                disabled={!agreed || !password || loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {loading ? '처리 중' : '탈퇴하기'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountModal;
